import { apiCall } from '@/lib/api';

export interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  cancel_at?: number;
  canceled_at?: number;
  items: {
    data: Array<{
      current_period_end: number;
      price: {
        id: string;
        product: {
          name: string;
        };
        unit_amount: number;
        recurring: {
          interval: string;
        };
      };
    }>;
  };
}

export interface PaymentMethod {
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}

export interface Invoice {
  id: string;
  amount_due: number;
  amount_paid: number;
  status: string;
  created: number;
  invoice_pdf: string;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  price: {
    id: string;
    amount: number;
    currency: string;
    interval: string;
    intervalCount: number;
  };
}

class BillingService {
  private readonly API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';

  async getSubscription(): Promise<Subscription> {
    const response = await apiCall<Subscription>(`${this.API_URL}/billing/subscription`, {
      method: 'POST'
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    //if (!response.data) {
    //  throw new Error('No data received from server');
    //}
    return response.data;
  }

  async getPaymentMethod(): Promise<PaymentMethod> {
    const response = await apiCall<PaymentMethod>(`${this.API_URL}/billing/payment-method`, {
      method: 'POST'
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    //if (!response.data) {
    //  throw new Error('No data received from server');
    //}
    return response.data;
  }

  async getInvoices(): Promise<Invoice[]> {
    const response = await apiCall<Invoice[]>(`${this.API_URL}/billing/invoices`, {
      method: 'POST'
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    //if (!response.data) {
    //  throw new Error('No data received from server');
    //}
    return response.data;
  }

  async getPlans(): Promise<Plan[]> {
    const response = await apiCall<Plan[]>(`${this.API_URL}/billing/plans`, {
      method: 'POST'
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  }

  async createSubscription(priceId: string): Promise<{ clientSecret: string }> {
    const response = await apiCall<{ clientSecret: string }>(`${this.API_URL}/billing/create-subscription`, {
      method: 'POST',
      body: JSON.stringify({ priceId })
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  }

  async confirmSubscription(paymentIntentId: string): Promise<void> {
    const response = await apiCall(`${this.API_URL}/billing/confirm-subscription`, {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId })
    });
    if (!response.success) {
      throw new Error(response.message);
    }
  }

  async updateSubscription(subscriptionId: string, priceId: string): Promise<{ clientSecret: string }> {
    const response = await apiCall<{ clientSecret: string }>(`${this.API_URL}/billing/update-subscription`, {
      method: 'POST',
      body: JSON.stringify({ subscriptionId, priceId })
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  }

  async cancelSubscription(subscriptionId: string | undefined): Promise<void> {
    if (!subscriptionId) {
      throw new Error('No subscription ID provided');
    }
    const response = await apiCall(`${this.API_URL}/billing/cancel-subscription`, {
      method: 'POST',
      body: JSON.stringify({ subscriptionId })
    });
    if (!response.success) {
      throw new Error(response.message);
    }
  }

  async reactivateSubscription(subscriptionId: string | undefined): Promise<void> {
    if (!subscriptionId) {
      throw new Error('No subscription ID provided');
    }
    const response = await apiCall(`${this.API_URL}/billing/reactivate-subscription`, {
      method: 'POST',
      body: JSON.stringify({ subscriptionId })
    });
    if (!response.success) {
      throw new Error(response.message);
    }
  }

  async createSetupIntent(): Promise<{ clientSecret: string }> {
    const response = await apiCall<{ clientSecret: string }>(`${this.API_URL}/billing/create-setup-intent`, {
      method: 'POST'
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  }

  async downloadInvoice(invoiceId: string): Promise<Blob> {
    const response = await fetch(`${this.API_URL}/billing/download-invoice/${invoiceId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('sb-access-token')}`,
        'X-Refresh-Token': localStorage.getItem('sb-refresh-token') || ''
      }
    });
    if (!response.ok) {
      throw new Error('Failed to download invoice');
    }
    return response.blob();
  }

  async createCheckoutSession(priceId: string, isUpgrade: boolean, source?: string): Promise<{ url: string }> {
    const payload: { priceId: string; isUpgrade: boolean; source?: string } = { priceId, isUpgrade };
    if (source) {
      payload.source = source;
    }
    const response = await apiCall<{ url: string }>(`${this.API_URL}/billing/create-checkout-session`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  }

  async sendFirstInvoice(payload: {
    email: string;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    postalCode: string;
    province: string;
    country: string;
  }): Promise<{
    customerId: string;
    subscriptionId: string;
    invoiceId: string | null;
    hostedInvoiceUrl: string | null;
  }> {
    const response = await apiCall<{
      customerId: string;
      subscriptionId: string;
      invoiceId: string | null;
      hostedInvoiceUrl: string | null;
    }>(`${this.API_URL}/billing/send-first-invoice`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  }

  async createAddOnCheckoutSession(tierNumber: number, isUpgrade: boolean): Promise<{ url: string }> {
    const response = await apiCall<{ url: string }>(`${this.API_URL}/billing/create-addon-checkout-session`, {
      method: 'POST',
      body: JSON.stringify({ tierNumber, isUpgrade })
    });
    if (!response.success) {
      throw new Error(response.message);
    }
    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  }
}

export const billingService = new BillingService(); 